/*
 * Copyright (c) 2025 curryfirm.com
 */

import React, { useEffect } from 'react';
import Typography from "@mui/material/Typography";
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchUniqueLocationsLoggedThunk,
    selectAllLocations,
    selectLocationsAreLoading,
    selectDropLotLocations,
    selectLocationsError,
} from "../../../features/locations/locationsSlice.js";
import {
    setLocationChoosingMode,
    selectWizardLocationId,
    setLocationDropLot,
    setLocationCustomAddress,
    selectReturnToSummaryAvailable,
    setReturnToSummaryAvailable,
    setStep,
    WIZARD_STEPS,
    selectStoredRouteType,
} from '../../../features/workflow/workflowSlice.js';
import * as constants from '../../../constants.js';
import {
    Radio,
    FormControlLabel,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Button,
    Box,
    Alert,
} from '@mui/material';
import {ROUTE_TYPES} from "../../../constants.js";
import {selectEmptyAvailableDumpsterDetails} from "../../../features/workflow/workflowSelectors.js";

const StartEndLocationChooser = ({ label, onBack, onContinue }) => {
    const isStart = label.includes(constants.START_LOCATION_STEP_LABEL);
    const isSwapAndDrop = (useSelector(selectStoredRouteType) === ROUTE_TYPES.SWAP_AND_DROP);
    const dispatch = useDispatch();

    const loading = useSelector(selectLocationsAreLoading);
    const locationsError = useSelector(selectLocationsError);
    const uniqueLocations = useSelector(selectAllLocations);
    const dropLots = useSelector(selectDropLotLocations);
    const emptyCanDetails = useSelector(selectEmptyAvailableDumpsterDetails)?.[0];

    // Dynamic field keys in store state according to what wizard step we're in
    const modeField = isStart ? 'startLocationChoosingMode' : 'endLocationChoosingMode';
    const dropLotField = isStart ? 'startLocationDropLot' : 'endLocationDropLot';
    const customAddressField = isStart ? 'startLocationCustomAddress' : 'endLocationCustomAddress';

    // Gather current values from Redux store
    const mode = useSelector(state => state.workflow[modeField]);
    const chosenDropLot = useSelector(state => state.workflow[dropLotField]);
    const customAddress = useSelector(state => state.workflow[customAddressField]);
    const returnToSummaryAvailable = useSelector(selectReturnToSummaryAvailable);
    const currentLocationKnown = (useSelector(selectWizardLocationId) !== null);

    // Ensure known locations are loaded.  Testing with "2" because the user's current location
    // should have been loaded from the URL parameters.
    // TODO: Improve test, because the app can run with no location params passed.  In that case
    // we should test against 1.
    useEffect(() => {
        if (uniqueLocations.length < 2 && !loading) {
            dispatch(fetchUniqueLocationsLoggedThunk());
        }
    }, [dispatch, uniqueLocations.length, loading]);
    if (locationsError) {
        return <Alert severity="error">{locationsError}</Alert>;
    }
    if (loading) {
        return <Typography variant={"body1"}>Loading unique locations...</Typography>;
    }

    const handleReturnToSummary = () => {
        dispatch(setStep(WIZARD_STEPS.SUMMARY));
        dispatch(setReturnToSummaryAvailable(false)); // Reset flag on return
    };

    if (isStart && isSwapAndDrop) {
        return (
            <Box>
                <Typography variant="h4" gutterBottom>{label}</Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                    {emptyCanDetails
                        ? `This route will start at the location of your empty dumpster:
             ${emptyCanDetails.customer}: ${emptyCanDetails.street}, ${emptyCanDetails.city}`
                        : 'Weird! No empty dumpster has been selected.  Go back and pick one..'}
                </Alert>
                <Box sx={{ mt: 3 }}>
                    <Button variant="contained" color="primary" onClick={onBack}>Back</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onContinue}
                        disabled={!emptyCanDetails}
                        sx={{ ml: 2 }}
                    >
                        Next
                    </Button>
                </Box>
            </Box>
        );
    }
    else {
        return (
            <Box>
                {/* Title */}
                <FormLabel component="legend" sx={{mb: 2}}>
                    <Typography variant="h4" gutterBottom> {label}</Typography>
                </FormLabel>

                {/* Current location radio, only if current location is known */}
                {currentLocationKnown ? (
                    <FormControl fullWidth margin="dense">
                        <FormControlLabel
                            value={constants.LOCATION_CHOOSING_MODES.current}
                            control={
                                <Radio
                                    checked={mode === constants.LOCATION_CHOOSING_MODES.current}
                                    onChange={() => dispatch(setLocationChoosingMode({
                                        field: modeField,
                                        value: constants.LOCATION_CHOOSING_MODES.current
                                    }))}
                                />
                            }
                            label="Current Location"
                        />
                    </FormControl>
                ) : null}

                {/* Pick a Drop-lot (dropdown - acts as mode selector) */}
                <FormControl fullWidth margin="dense">
                    <Select
                        value={mode === constants.LOCATION_CHOOSING_MODES.dropLot ? (chosenDropLot ?? "") : ""}
                        displayEmpty
                        size="small"
                        sx={{minWidth: 220}}
                        onFocus={() => dispatch(setLocationChoosingMode({
                            field: modeField,
                            value: constants.LOCATION_CHOOSING_MODES.dropLot
                        }))}
                        onChange={e => {
                            dispatch(setLocationChoosingMode({
                                field: modeField,
                                value: constants.LOCATION_CHOOSING_MODES.dropLot
                            }));
                            dispatch(setLocationDropLot({field: dropLotField, value: e.target.value}));
                        }}
                        renderValue={selected => selected ? dropLots.find(lot => lot.key === selected)?.street : "Pick a Drop-lot"}
                    >
                        <MenuItem value="" disabled>
                            Pick a Drop-lot
                        </MenuItem>
                        {dropLots?.map(lot => (
                            <MenuItem key={lot.key}
                                      value={lot.key}>{lot.customer}: {lot.street}, {lot.city} ({lot.lat}, {lot.lng})</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Pick a Known Address (dropdown - acts as selector) */}
                <FormControl fullWidth margin="dense">
                    <Select
                        value={mode === constants.LOCATION_CHOOSING_MODES.custom ? (customAddress ?? "") : ""}
                        displayEmpty
                        size="small"
                        sx={{minWidth: 220}}
                        onFocus={() => dispatch(setLocationChoosingMode({
                            field: modeField,
                            value: constants.LOCATION_CHOOSING_MODES.custom
                        }))}
                        onChange={e => {
                            dispatch(setLocationChoosingMode({
                                field: modeField,
                                value: constants.LOCATION_CHOOSING_MODES.custom
                            }));
                            dispatch(setLocationCustomAddress({field: customAddressField, value: e.target.value}));
                        }}
                        renderValue={selected => {
                            if (selected) {
                                const locObj = uniqueLocations.find(loc => loc.key === selected);
                                return `${locObj?.customer}: ${locObj?.street}, ${locObj?.city}`;
                            } else {
                                return "Pick a Known Address";
                            }
                        }}
                    >
                        <MenuItem value="" disabled>
                            Pick a Known Address
                        </MenuItem>
                        {uniqueLocations?.map(loc => (
                            <MenuItem key={loc.key}
                                      value={loc.key}>{loc.customer}: {loc.street}, {loc.city} ({loc.lat}, {loc.lng})</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{mt: 3, display: "flex", gap: 2}}>
                    <Button variant="contained" color="primary" onClick={onBack}>Back</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onContinue}
                        disabled={
                            (mode === constants.LOCATION_CHOOSING_MODES.dropLot && !chosenDropLot) ||
                            (mode === constants.LOCATION_CHOOSING_MODES.custom && !customAddress)
                        }
                    >
                        Next
                    </Button>

                    {/* Render a "Return to Summary" button conditionally, basd on store */}
                    {returnToSummaryAvailable && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleReturnToSummary}
                        >
                            Return to Summary
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }
};

export default StartEndLocationChooser;